/**
 * Daum(카카오) 우편번호/주소 검색 — 전 사이트 공용 모듈 ($j.F.Postcode).
 *
 * SDK 를 1회 동적 로드하고, 전체화면 오버레이에 위젯을 임베드한다.
 * 사이트별 CSS 에 의존하지 않도록 오버레이는 인라인 스타일로 자급자족(KakaoMap 패턴).
 * 색은 컴포넌트 토큰 --ui-postcode-{bg,ink,border,close-bg} 로 열어둔다 — 폴백이 기본 모습이고, 사이트 scss 가 :root 에서 잇는다.
 *
 * 사용:
 *   // 1) 콜백으로 직접 사용 — data 는 daum.Postcode oncomplete 객체
 *   $j.F.Postcode.open((data) => {
 *       console.log(data.roadAddress, data.jibunAddress, data.zonecode);
 *   });
 *
 *   // 2) [data-postcode] 인풋 자동 바인딩 — 클릭 시 검색 → 선택 주소를 value 로
 *   $j.F.Postcode.bindInputs($root);   // $root 스코프로 이벤트 위임 (기본 document)
 *   //   <input name="address" data-postcode readonly placeholder="주소 검색">
 *   //   data-postcode="jibun" 지정 시 지번주소 우선
 *
 * 사이트 옵션 — 사이트 부트스트랩에서 1회. 기본값은 전부 꺼짐(전폭 "닫기 ✕" 버튼, 히스토리 안 씀).
 *   $j.F.Postcode.init({ backClose, titleBar, backBar })
 *   backClose : 열 때 히스토리 항목을 하나 얹고 폰 뒤로가기(popstate)로 레이어만 닫는다(Ui/BS.js backClose 와 같은 방식).
 *               닫기 버튼·주소 선택으로 닫을 때는 그 항목을 history.back() 으로 회수한다 — 안 하면 다음 뒤로가기가 먹통이 된다.
 *   titleBar  : 머리를 왼쪽 제목 "주소 검색" + 오른쪽 × 아이콘 버튼으로 그린다(.j-postcode-layer__head).
 *   backBar   : 머리를 왼쪽 ‹ 뒤로 버튼 + 가운데 제목 "주소 검색" 으로 그린다 — 그 사이트의 하위화면 헤더와 같은 모양.
 *               titleBar 와 같이 켜면 backBar 가 이긴다.
 */
const SDK_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

export default class Postcode {
    constructor() {
        this._loading = null;   // SDK 로드 Promise (중복로드 방지)
        this.$layer = null;     // 오버레이 (지연생성·재사용)
        this._backClose = false;
        this._titleBar = false;
        this._backBar = false;
        this._histOn = false;   // 뒤로가기용 히스토리 항목을 얹어둔 상태인가 (backClose 일 때만)
        this._onPop = null;
    }

    /**
     * 사이트 옵션 — 사이트 부트스트랩에서 1회. 이미 만든 레이어가 있으면 다음 open 때 새 옵션으로 다시 만든다.
     * @param {{backClose?: boolean, titleBar?: boolean, backBar?: boolean}} option
     */
    init(option = {}) {
        if (this._histOn || this._onPop) this.close();   // 열린 채 옵션이 바뀌면 히스토리 항목·리스너부터 회수
        this._backClose = option.backClose === true;
        this._titleBar = option.titleBar === true;
        this._backBar = option.backBar === true;
        if (this.$layer) { this.$layer.remove(); this.$layer = null; }
    }

    /** Daum 우편번호 SDK 를 1회 로드. */
    load() {
        if (window.daum && window.daum.Postcode) return Promise.resolve();
        if (this._loading) return this._loading;

        this._loading = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = SDK_SRC;
            s.onload = () => resolve();
            s.onerror = () => { this._loading = null; reject(new Error('Daum Postcode SDK load failed')); };
            document.head.appendChild(s);
        });
        return this._loading;
    }

    /**
     * 주소검색 위젯을 오버레이로 띄운다.
     * @param {function|object} opts 콜백함수 또는 { onComplete } — oncomplete(data) 로 호출
     * @returns {Promise<void>}
     */
    open(opts = {}) {
        const onComplete = typeof opts === 'function' ? opts : opts.onComplete;

        return this.load().then(() => {
            const $frame = this._ensureLayer().find('.j-postcode__frame').empty();
            new window.daum.Postcode({
                width: '100%',
                height: '100%',
                oncomplete: (data) => {
                    this.close();
                    if (typeof onComplete === 'function') onComplete(data);
                },
            }).embed($frame[0]);
            this.$layer.css('display', 'flex');
            if (this._backClose) this._pushHistory();
        });
    }

    /**
     * 오버레이 닫기.
     * @param {boolean} fromPop 뒤로가기(popstate)로 들어온 호출인가 — true 면 히스토리는 이미 되감겨 있다
     */
    close(fromPop = false) {
        if (this.$layer) this.$layer.css('display', 'none');
        this._offPop();
        const hadHist = this._histOn;
        this._histOn = false;
        if (hadHist && !fromPop) history.back();
    }

    /** 뒤로가기 몫의 히스토리 항목을 얹고 popstate 를 듣는다(이미 얹었으면 그대로) */
    _pushHistory() {
        if (this._histOn) return;
        this._histOn = true;
        history.pushState({ postcode: 1 }, '', location.href);
        this._onPop = () => {
            if (history.state && history.state.postcode) return;   // 위에 겹친 다른 레이어가 빠진 것 — 나는 그대로
            this.close(true);
        };
        window.addEventListener('popstate', this._onPop);
    }

    _offPop() {
        if (!this._onPop) return;
        window.removeEventListener('popstate', this._onPop);
        this._onPop = null;
    }

    /**
     * [data-postcode] 인풋 자동 바인딩 — 클릭 시 검색, 선택 주소를 value 로 채우고 change 발생.
     * data-postcode="jibun" 이면 지번주소 우선.
     * @param {string|Element|jQuery} root 이벤트 위임 스코프 (기본 document)
     * @returns {this}
     */
    bindInputs(root = document) {
        $(root).on('click', '[data-postcode]', (e) => {
            const $input = $(e.currentTarget);
            const jibunFirst = $input.data('postcode') === 'jibun';
            this.open((data) => {
                const road = data.roadAddress || data.address;
                $input.val(jibunFirst ? (data.jibunAddress || road) : road).trigger('change');
            });
        });
        return this;
    }

    /** 오버레이 DOM 보장(지연생성·재사용). 사이트 CSS 비의존 인라인 스타일. */
    _ensureLayer() {
        if (this.$layer && document.body.contains(this.$layer[0])) return this.$layer;

        this.$layer = $(
            '<div class="j-postcode-layer">' +
              (this._backBar ? this._backBarHtml() : this._titleBar ? this._titleBarHtml() : '<button type="button" class="j-postcode-layer__close">닫기 ✕</button>') +
              '<div class="j-postcode__frame"></div>' +
            '</div>'
        ).css({
            position: 'fixed',
            inset: '0',
            zIndex: 'var(--z-index-modal, 1055)',
            maxWidth: '720px',
            margin: '0 auto',
            background: 'var(--ui-postcode-bg, #fff)',
            display: 'none',
            flexDirection: 'column',
            // 상태바·홈 인디케이터 띠 — 위젯이 iframe 이라 그 안에선 비울 수 없다. 사이트가 값을 준다(기본 0)
            boxSizing: 'border-box',
            paddingTop: 'var(--ui-postcode-top-inset, 0px)',
            paddingBottom: 'var(--ui-postcode-bottom-inset, 0px)',
        });

        const $close = this.$layer.find('.j-postcode-layer__close');
        if (this._backBar) {
            this._styleBackBar($close);
        } else if (this._titleBar) {
            const ink = 'var(--ui-postcode-ink, #222)';
            this.$layer.find('.j-postcode-layer__head').css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '52px',
                flexShrink: '0',
                padding: '0 4px 0 16px',
                borderBottom: '1px solid var(--ui-postcode-border, #e5e5e5)',
            });
            this.$layer.find('.j-postcode-layer__title').css({ fontSize: '16px', fontWeight: '700', color: ink });
            $close.css({
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'none',
                color: ink,
                cursor: 'pointer',
            });
        } else {
            $close.css({
                height: '44px',
                flexShrink: '0',
                border: 'none',
                background: 'var(--ui-postcode-close-bg, #f1f3f5)',
                color: 'var(--ui-postcode-ink, #222)',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
            });
        }
        $close.on('click', () => this.close());
        this.$layer.find('.j-postcode__frame').css('flex', '1');

        $('body').append(this.$layer);
        return this.$layer;
    }

    /** backBar 옵션의 머리 — 왼쪽 ‹ 뒤로 버튼 + 가운데 제목 */
    _backBarHtml() {
        return '<div class="j-postcode-layer__head">' +
                 '<button type="button" class="j-postcode-layer__close" aria-label="뒤로">' +
                   '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>' +
                 '</button>' +
                 '<span class="j-postcode-layer__title">주소 검색</span>' +
               '</div>';
    }

    /** backBar 머리 규격 — 높이 48 · 뒤로 버튼 48×48(왼쪽 4) · 제목 18px/600 가운데 */
    _styleBackBar($close) {
        const ink = 'var(--ui-postcode-ink, #222)';
        this.$layer.find('.j-postcode-layer__head').css({
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '48px',
            flexShrink: '0',
            borderBottom: '1px solid var(--ui-postcode-border, #e5e5e5)',
        });
        this.$layer.find('.j-postcode-layer__title').css({ fontSize: '18px', fontWeight: '600', letterSpacing: '-0.45px', color: ink });
        $close.css({
            position: 'absolute',
            top: '0',
            left: '4px',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0',
            border: 'none',
            background: 'none',
            color: ink,
            cursor: 'pointer',
        });
    }

    /** titleBar 옵션의 머리 — 왼쪽 제목 + 오른쪽 × 아이콘 버튼 */
    _titleBarHtml() {
        return '<div class="j-postcode-layer__head">' +
                 '<span class="j-postcode-layer__title">주소 검색</span>' +
                 '<button type="button" class="j-postcode-layer__close" aria-label="닫기">' +
                   '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
                 '</button>' +
               '</div>';
    }
}
